import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getStoredDeviceId } from '../../../utils/deviceIdStorage';

interface AuthState {
    logoutInProgress: boolean;
    principalTenant: string | null;
    principalUserId: string | null;
    tenant: string | null;
    userId: string | null;
    role: string | null;
    isImpersonating: boolean;
    isSuperadmin: boolean;
    deviceId: string | null;
    matrixAccessToken: string | null;
    matrixRefreshToken: string | null;
    matrixExpiresAtMs: number | null;
}

const initialState: AuthState = {
    logoutInProgress: false,
    principalTenant: null,
    principalUserId: null,
    tenant: null,
    userId: null,
    role: null,
    isImpersonating: false,
    isSuperadmin: false,
    deviceId: null,
    matrixAccessToken: null,
    matrixRefreshToken: null,
    matrixExpiresAtMs: null,
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        beginLogout: (state) => {
            state.logoutInProgress = true;
        },
        setAuthContext: (state, action: PayloadAction<{
            principalTenant: string;
            principalUserId: string;
            tenant: string;
            userId: string;
            role: string;
            isImpersonating: boolean;
            isSuperadmin: boolean;
        }>) => {
            state.principalTenant = action.payload.principalTenant;
            state.principalUserId = action.payload.principalUserId;
            state.tenant = action.payload.tenant;
            state.userId = action.payload.userId;
            state.role = action.payload.role;
            state.isImpersonating = action.payload.isImpersonating;
            state.isSuperadmin = action.payload.isSuperadmin;
            state.deviceId = getStoredDeviceId(action.payload.tenant, action.payload.userId);
        },
        setMatrixAccessToken: (state, action: PayloadAction<string | null>) => {
            state.matrixAccessToken = action.payload;
        },
        setMatrixSession: (state, action: PayloadAction<{
            accessToken: string | null;
            refreshToken: string | null;
            expiresAtMs: number | null;
        }>) => {
            state.matrixAccessToken = action.payload.accessToken;
            state.matrixRefreshToken = action.payload.refreshToken;
            state.matrixExpiresAtMs = action.payload.expiresAtMs;
        },
        clearAuthData: (state) => {
            state.principalTenant = null;
            state.principalUserId = null;
            state.tenant = null;
            state.userId = null;
            state.role = null;
            state.isImpersonating = false;
            state.isSuperadmin = false;
            state.deviceId = null;
            state.matrixAccessToken = null;
            state.matrixRefreshToken = null;
            state.matrixExpiresAtMs = null;
        },
        initializeDeviceId: (state, action: PayloadAction<{ tenant: string | null; userId: string | null } | undefined>) => {
            const tenant = action.payload?.tenant ?? state.tenant;
            const userId = action.payload?.userId ?? state.userId;
            state.deviceId = getStoredDeviceId(tenant, userId);
        },
    },
});

export const {
    beginLogout,
    setAuthContext,
    clearAuthData,
    initializeDeviceId,
    setMatrixAccessToken,
    setMatrixSession,
} = authSlice.actions;
export const selectLogoutInProgress = (state: { auth: { logoutInProgress?: boolean } }) =>
    state.auth.logoutInProgress === true;
export const selectUserId = (state: { auth: { userId: string } }) => state.auth.userId;
export const selectTenant = (state: { auth: { tenant: string } }) => state.auth.tenant;
export const selectPrincipalUserId = (state: { auth: { principalUserId: string | null } }) => state.auth.principalUserId;
export const selectPrincipalTenant = (state: { auth: { principalTenant: string | null } }) => state.auth.principalTenant;
export const selectUserRole = (state: { auth: { role: string } }) => state.auth.role;
export const selectIsImpersonating = (state: { auth: { isImpersonating: boolean } }) => state.auth.isImpersonating;
export const selectIsSuperadmin = (state: { auth: { role: string | null; isSuperadmin: boolean; isImpersonating: boolean } }) =>
    state.auth.isSuperadmin === true || state.auth.role === 'SUPERADMIN' || state.auth.isImpersonating === true;
export const selectDeviceId = (state: { auth: { deviceId: string | null } }) => state.auth.deviceId;
export const selectMatrixAccessToken = (state: { auth: { matrixAccessToken: string | null } }) => state.auth.matrixAccessToken;
export const selectMatrixRefreshToken = (state: { auth: { matrixRefreshToken: string | null } }) => state.auth.matrixRefreshToken;
export const selectMatrixExpiresAtMs = (state: { auth: { matrixExpiresAtMs: number | null } }) => state.auth.matrixExpiresAtMs;

export interface PersistedMatrixSession {
    accessToken: string | null;
    refreshToken: string | null;
    expiresAtMs: number | null;
}

const AUTH_PERSIST_KEY = 'persist:auth';
const EMPTY_MATRIX_SESSION: PersistedMatrixSession = {
    accessToken: null,
    refreshToken: null,
    expiresAtMs: null,
};

// Matrix refresh tokens are single-use, and only the window holding the session
// lock rotates them. redux-persist rehydrates once at page load, so another
// window's in-memory store goes stale after any rotation. Read the persisted
// state directly whenever the freshest token pair is required (e.g. when a
// standby window takes over the Matrix session). The tokens are only returned
// when the persisted auth still belongs to the expected account — another
// window may have logged into a different one.
export function readPersistedMatrixSession(expected: { tenant: string; userId: string }): PersistedMatrixSession {
    try {
        const raw = localStorage.getItem(AUTH_PERSIST_KEY);
        if (!raw) return EMPTY_MATRIX_SESSION;

        // redux-persist stores each slice field as its own JSON string
        const persisted = JSON.parse(raw) as Record<string, string | undefined>;
        const parseField = <T,>(field: string | undefined): T | null => {
            if (field === undefined) return null;
            return (JSON.parse(field) as T | null) ?? null;
        };

        const tenant = parseField<string>(persisted.tenant);
        const userId = parseField<string>(persisted.userId);
        if (tenant !== expected.tenant || userId !== expected.userId) {
            return EMPTY_MATRIX_SESSION;
        }

        return {
            accessToken: parseField<string>(persisted.matrixAccessToken),
            refreshToken: parseField<string>(persisted.matrixRefreshToken),
            expiresAtMs: parseField<number>(persisted.matrixExpiresAtMs),
        };
    } catch {
        return EMPTY_MATRIX_SESSION;
    }
}

export default authSlice.reducer;
